import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, X, ArrowUpRight, ArrowRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav, MarketingFooter, useSeoMeta, MAILTO } from "@/components/MarketingChrome";
import { SEO_PAGES } from "@/data/seoPages";

function Cell({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-[hsl(var(--chart-2))] mx-auto" strokeWidth={2.5} />;
  if (value === false) return <X className="h-4 w-4 text-muted-foreground/40 mx-auto" strokeWidth={2} />;
  return <span className="font-mono text-[10px] text-muted-foreground">{value}</span>;
}

export default function SeoLanding({ slug }) {
  const params = useParams();
  const key = slug || params.slug;
  const page = SEO_PAGES[key];

  useSeoMeta(page?.title || "Vaultvec", page?.metaDescription || "");
  useEffect(() => { window.scrollTo(0, 0); }, [key]);

  if (!page) return <Navigate to="/" replace />;

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="ambient-spot top-0 right-[8%] w-[460px] h-[360px] opacity-50" />
        <div className="relative max-w-4xl mx-auto px-6">
          <span className="eyebrow-line font-mono text-xs tracking-[0.2em] uppercase text-primary">{page.eyebrow}</span>
          <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.98]">{page.h1}</h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">{page.subhead}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/#pricing"><Button data-testid="seo-cta-pricing" size="lg" className="rounded-sm bg-primary hover:bg-primary/90 text-white h-12 px-8 glow-primary hover:glow-primary-strong transition-shadow">See plans <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></Button></Link>
            <Link to="/resources"><Button data-testid="seo-cta-resources" size="lg" variant="outline" className="rounded-sm border-white/15 h-12 px-8 hover:bg-white/5 hover:border-white/30">Free resources</Button></Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-12 space-y-6">
        {page.intro.map((p, i) => (
          <p key={i} className="text-base text-muted-foreground leading-relaxed">{p}</p>
        ))}
      </section>

      {/* COMPARISON (optional) */}
      {page.comparison && (
        <section className="max-w-4xl mx-auto px-6 py-12">
          <div className="rounded-sm border border-white/10 overflow-hidden">
            <div className="grid grid-cols-[1.6fr_1fr_1fr] bg-[#0a0a0c] border-b border-white/10">
              <div className="p-4 font-mono text-[10px] tracking-widest text-muted-foreground">CAPABILITY</div>
              <div className="p-4 text-center border-l border-white/10 bg-primary/10 relative">
                <div className="absolute inset-x-0 top-0 h-px bg-primary/60" />
                <span className="font-heading font-bold tracking-tight text-primary">Vaultvec</span>
              </div>
              <div className="p-4 text-center border-l border-white/10 font-mono text-[10px] tracking-widest text-muted-foreground flex items-center justify-center">LEGACY SUITES</div>
            </div>
            {page.comparison.map((row) => (
              <div key={row.feature} className="grid grid-cols-[1.6fr_1fr_1fr] border-b border-white/10 last:border-0 hover:bg-white/[0.02] transition-colors">
                <div className="p-4 text-sm">{row.feature}</div>
                <div className="p-4 flex items-center justify-center border-l border-white/10 bg-primary/[0.05]"><Cell value={row.v} /></div>
                <div className="p-4 flex items-center justify-center border-l border-white/10"><Cell value={row.legacy} /></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SECTIONS */}
      <section className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        {page.sections.map((s) => (
          <div key={s.h2}>
            <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl">{s.h2}</h2>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">{s.body}</p>
          </div>
        ))}
      </section>

      {/* FAQ */}
      {page.faqs && (
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl mb-8">Frequently asked</h2>
          <div className="space-y-4">
            {page.faqs.map((f) => (
              <div key={f.q} className="card-elevated rounded-sm p-6">
                <h3 className="font-semibold">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="relative border-t border-white/10 overflow-hidden">
        <div className="ambient-spot top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] opacity-20" />
        <div className="relative max-w-4xl mx-auto px-6 py-20 flex flex-col items-start gap-6">
          <h2 className="font-heading font-bold tracking-tighter text-3xl sm:text-4xl">Make your next FDA inspection a non-event.</h2>
          <p className="text-muted-foreground leading-relaxed max-w-xl">See how Vaultvec unifies backup, threat defense and 21 CFR Part 11 compliance in one platform.</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/#how"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 text-white h-12 px-8 glow-primary">See how it works <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></Button></Link>
            <a href={MAILTO}><Button size="lg" variant="outline" className="rounded-sm border-white/15 h-12 px-8 hover:bg-white/5"><Mail className="mr-2 h-4 w-4" strokeWidth={1.5} /> Contact us</Button></a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
